import styled from 'styled-components';

import {getLocalizationService} from '~/shared/services/localisationService';
import {GeolocationPlace} from '~/shared/services/geocoder';
import ClickOutsideDiv from '~/shared/components/ClickOutsideDiv';
import {getBoldedAddressPartHtml} from '~/shared/utils/address';
import {GoogleMapsAPIProvider} from '~/shared/components/Maps';
import {AddressFinderVariant} from '~/shared/consts/addressConsts';
import CloseCircleIcon from '~/assets/images/icons/closeCircle.svg?react';
import {AddressFinderLocation} from '~/shared/services/analytics/analyticsModels';

import {
  DropdownContainer,
  DropdownContainerWrapper,
  Input,
  SuggestionItem,
  BoldResultContainer,
  PoweredByGoogleWrapper,
  PoweredByGoogleImg,
  SuggestionItemIcon,
  ScreenReaderNotificator,
  StyledSearchIconSvg,
  StyledLocationIconSvg,
  StyledTargetIcon,
  StyledSuggestionLocationIcon,
  ClearButtonWrapper,
  StyledClearButton,
} from './CustomAddressAutoComplete.styled';
import useAddressAutocomplete from './useAddressAutocomplete';
import {CURRENT_LOCATION_PLACE_ID} from './consts';

const Root = styled(ClickOutsideDiv)`
  width: 100%;
`;

const SuggestionResultWithBoldParts = ({matchedSubstrings, description}: {
  matchedSubstrings: google.maps.places.StringRange[];
  description: string;
}) => {
  if (!matchedSubstrings) {
    return <>{description}</>;
  }

  return (
    <BoldResultContainer
      dangerouslySetInnerHTML={{__html: getBoldedAddressPartHtml(matchedSubstrings, description)}}
    />
  );
};

const SuggestionIcon = ({isCurrentLocation, variant}: {isCurrentLocation?: boolean; variant: AddressFinderVariant}) => {
  if (variant === AddressFinderVariant.LEGACY) {
    return <SuggestionItemIcon isCurrentLocation={isCurrentLocation} />;
  }
  if (isCurrentLocation) {
    return <StyledTargetIcon/>;
  }
  return <StyledSuggestionLocationIcon/>;
};

type CustomAddressAutoCompleteProps = {
  inputRef: (domEl: HTMLInputElement) => void;
  placeholder?: string;
  resetAddress?: () => void;
  isMaxMobile?: boolean;
  isAddAddressMode?: boolean;
  onCurrentLocationClick?: ({setAddress}: {setAddress: (address: string) => void}) => Promise<void>;
  onPlaceSelected: (place: GeolocationPlace) => Promise<void>;
  variant: AddressFinderVariant;
  isError?: boolean;
  showAddressNumberInput?: boolean;
  location: AddressFinderLocation;
};

const CustomAddressAutoCompleteComponent = ({
  variant,
  inputRef,
  placeholder,
  resetAddress,
  isMaxMobile,
  isAddAddressMode,
  onCurrentLocationClick,
  onPlaceSelected,
  isError,
  showAddressNumberInput,
  location,
}: CustomAddressAutoCompleteProps) => {
  const {t} = getLocalizationService();
  const {
    address,
    isLoaded,
    isInputFocused,
    currentAddressKey,
    predictions,
    highlightedIndex,
    onChange,
    handleSelect,
    onInputClick,
    onInputBlur,
    onInputFocus,
    onInputKeyDown,
    isDropdownOpen,
    closeDropdown,
    clearInput,
  } = useAddressAutocomplete({
    onCurrentLocationClick,
    onPlaceSelected,
    resetAddress,
    isMaxMobile,
    location,
  });

  if (!isLoaded) {
    return null;
  }

  const IconComponent = variant === AddressFinderVariant.PIE ? StyledLocationIconSvg : StyledSearchIconSvg;
  const showClearButton = Boolean(variant === AddressFinderVariant.PIE && address && !showAddressNumberInput);

  return (
    <Root onClickOutside={closeDropdown}>
      <ScreenReaderNotificator>{t('type_address_for_delivery')}</ScreenReaderNotificator>
      <Input
        variant={variant}
        value={address}
        autoFocus={isAddAddressMode && !currentAddressKey}
        type="text"
        role="combobox"
        aria-controls="homePage_DropDownAddress"
        aria-autocomplete="list"
        aria-expanded={isInputFocused}
        autoComplete="off"
        isAddAddressMode={isAddAddressMode}
        ref={inputRef}
        onChange={onChange}
        onClick={onInputClick}
        onBlur={onInputBlur}
        onFocus={onInputFocus}
        onKeyDown={onInputKeyDown}
        placeholder={placeholder}
        isError={isError}
        withPadingLeft={showClearButton}
      />
      <IconComponent />
      {showClearButton && (
        <ClearButtonWrapper>
          <StyledClearButton variant="ghostSmall" iconButton onClick={clearInput}>
            <CloseCircleIcon />
          </StyledClearButton>
        </ClearButtonWrapper>
      )}
      {isDropdownOpen && (
        <DropdownContainerWrapper variant={variant}>
          <DropdownContainer data-test-id="address-dropdown" variant={variant}>
            {predictions.map((suggestion, index) => {
              if (!suggestion) {
                return null;
              }
              const isCurrentLocation = suggestion.placeId === CURRENT_LOCATION_PLACE_ID;
              const isHighlighted = index === highlightedIndex;
              return (
                <SuggestionItem
                  isCurrentLocation={isCurrentLocation}
                  id={suggestion.placeId}
                  key={suggestion.placeId}
                  isAddAddressMode={isAddAddressMode}
                  active={isHighlighted}
                  aria-selected={isHighlighted}
                  onClick={() => handleSelect(suggestion.text.text, suggestion.placeId)}
                  variant={variant}
                >
                  <SuggestionIcon isCurrentLocation={isCurrentLocation} variant={variant} />

                  <SuggestionResultWithBoldParts
                    matchedSubstrings={suggestion.text.matches}
                    description={suggestion.text.text}
                  />
                </SuggestionItem>
              );
            })}
            <PoweredByGoogleWrapper shouldShow={predictions.length > 1}>
              <PoweredByGoogleImg alt="Powered by google icon" />
            </PoweredByGoogleWrapper>
          </DropdownContainer>
        </DropdownContainerWrapper>
      )}
    </Root>
  );
};

const CustomAddressAutoComplete = (props: CustomAddressAutoCompleteProps) => {
  return (
    <GoogleMapsAPIProvider>
      <CustomAddressAutoCompleteComponent {...props} />
    </GoogleMapsAPIProvider>
  );
};

export default CustomAddressAutoComplete;
